import React, { Fragment, ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../../../components/atoms/translated_text';
import { Translations, getSupplyTypes } from './configuration';
import {
  AppSelect,
  AppTextField,
} from '../../../../../../components/molecules/form_controls';
import GridLayout from '../../../../../../components/molecules/grid_layout';
import { getState } from '../../../../../../redux/store';
import { ReducerKeys } from '../../../../../../redux/reducers';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { SchemeTypes } from 'config';
import VatReturnDetailsActions from 'redux/reducers/vatReturn_detail_reducer/actions';
import * as VatReturnDetailsSelectors from 'redux/reducers/vatReturn_detail_reducer/selectors';
import ModalActions from 'redux/reducers/gl_modal_reducer/actions';
import { ModalIDs } from 'redux/reducers/gl_modal_reducer/types';
import { StateStatus } from 'redux/utils/common';

interface IProps {
  vat_return_id: number;
  schemeType: SchemeTypes;
}

interface FormFields {
  period: string | null;
  year: number | null;
  consuptionCountry: string | null;
  amount: number | null;
  cents: number | null;
}

interface IQuarters {
  quarter_id: number;
  starting_month: number;
  ending_month: number;
  short_text: string;
}

interface IVatCorrection {
  vat_correction_id: number;
  vat_return_id: number;
  period_id: number;
  period: string;
  country_id: number;
  country: string;
  amount: number;
  year: number;
}

export default function AddVatReturnCorrectionForm(
  props: IProps
): ReactElement {
  // console.log('Render AddVatReturnCorrectionForm');
  const dispatch = useDispatch();
  const vatReturnDetailPeriod =
    VatReturnDetailsSelectors.useGetVatReturnDetailPeriod();

  const defaultValues = {
    period: null,
    year: null,
    consuptionCountry: null,
    amount: null,
    cents: null,
  };

  const { errors, control, handleSubmit, watch, setError, setValue } =
    useForm<FormFields>({
      defaultValues,
    });
  const classes = useStyles();
  const watchCountry = watch('consuptionCountry');
  const watchPeriod = watch('period');
  const watchYear = watch('year');

  React.useEffect(() => {
    dispatch(VatReturnDetailsActions.fetchQuarters());
    dispatch(VatReturnDetailsActions.fetchMonthQuarters());
  }, [dispatch]);

  React.useEffect(() => {
    setValue('consuptionCountry', null);
    if (watchPeriod && watchYear) {
      dispatch(
        VatReturnDetailsActions.fetchCorrectionsCountriesAction({
          vat_return_id: props.vat_return_id,
          quarter_id: Number.parseInt(watchPeriod),
          year: watchYear,
        })
      );
    } else {
      dispatch(VatReturnDetailsActions.emptyCorrectionsCountriesAction());
    }
  }, [dispatch, watchPeriod, watchYear]);

  React.useEffect(() => {
    return () => {
      dispatch(VatReturnDetailsActions.emptyCorrectionsCountriesAction());
    };
  }, []);

  React.useEffect(() => {
    if (watchYear && `${watchYear}` === `${vatReturnDetailPeriod.year}`) {
      setValue('period', null);
    }
  }, [vatReturnDetailPeriod, watchYear]);

  const onSubmit = (data: any) => {
    let countries = getState(
      ReducerKeys.VATRETURN_DETAIL_REDUCER
    ).correctionsCountries;
    let periodQuarters = getState(
      ReducerKeys.VATRETURN_DETAIL_REDUCER
    ).quarters;
    let monthQuarters = getState(
      ReducerKeys.VATRETURN_DETAIL_REDUCER
    ).monthQuarterList;

    let quarters = null;
    if (props.schemeType === SchemeTypes.IOSS) {
      quarters = monthQuarters;
    } else if (
      props.schemeType === SchemeTypes.EU ||
      props.schemeType === SchemeTypes.NON_EU
    ) {
      quarters = periodQuarters;
    }
    let quarter = null;
    for (let currentQuarter of quarters.data) {
      if (currentQuarter.quarter_id === data.period) {
        quarter = currentQuarter;
        break;
      }
    }

    const result = countries.data.find(
      (obj: { country_id: number }) => obj.country_id === Number(watchCountry)
    );

    // const due_balance = countries.data[data.consuptionCountry - 1].due_balance;
    let due_balance = null;
    try {
      due_balance = result.due_balance;
    } catch (error) {
      due_balance = null;
    }

    var amount = Number.parseInt(data.amount);
    if (amount >= 0 && data.amount[0] !== '-')
      amount += Number.parseInt(data.cents) / 100;
    else amount -= Number.parseInt(data.cents) / 100;

    if (due_balance && amount < 0 && Math.abs(amount) > due_balance) {
      setError('amount', {
        type: 'manual',
        message: `${Translations.amount_error.English}${due_balance}`,
      });
      return;
    }

    if (!due_balance && amount < 0) {
      setError('amount', {
        type: 'manual',
        message: `You can not enter negative amount for this country.`,
      });
      return;
    }

    let newData: Array<IVatCorrection> = new Array<IVatCorrection>();
    const vatReturnPeriod = getState(ReducerKeys.VATRETURN_DETAIL_REDUCER)
      .vatReturnDetail.data.period;
    let reduxData = getState(ReducerKeys.VATRETURN_DETAIL_REDUCER)
      .vatReturnDetail.vatCorrections;

    const isPeriodValid = correctionPeriodValidations(
      data.year,
      quarter,
      vatReturnPeriod
    );
    if (!isPeriodValid) return;

    for (let item of reduxData) {
      console.log('irta');
      const temp = countries.data.find(
        (obj: { country_id: number }) =>
          obj.country_id === data.consuptionCountry
      );
      if (
        temp &&
        item.country_id === temp.country_id &&
        item.period_id === data.period &&
        `${item.year}` === `${data.year}`
      ) {
        setError('consuptionCountry', {
          type: 'manual',
          message: `You already have a correction for the selected country at the selected period`,
        });
        setError('period', {
          type: 'manual',
          message: `You already have a correction for the selected country at the selected period`,
        });
        return;
      }
      newData.push(item);
    }

    const temp = countries.data.find(
      (obj: { country_id: number }) => obj.country_id === data.consuptionCountry
    );

    let allCountries = getState(ReducerKeys.VATRETURN_DETAIL_REDUCER).countries;
    const countryTemp = allCountries.data.find(
      (obj: { country_id: number }) => obj.country_id === data.consuptionCountry
    );

    // OBJECT
    let object: IVatCorrection = {
      vat_correction_id: 0,
      vat_return_id: props.vat_return_id,
      period_id: quarter.quarter_id,
      period: quarter.short_text,
      country_id: data.consuptionCountry,
      country: temp ? temp.country : countryTemp.country,
      amount: amount,
      year: data.year,
    };

    newData.push(object);

    dispatch(VatReturnDetailsActions.setVatCorrections(newData));
    msconChanges(object);
    dispatch(
      ModalActions.closeModalAction({
        modalID: ModalIDs.MODAL_ADD_VAT_RETURN_CORRECTIONS,
      })
    );
  };

  const correctionPeriodValidations = (
    year: string,
    quarter: IQuarters,
    currentVatReturnPeriod: {
      quarter: IQuarters;
      year: number;
    }
  ): boolean => {
    const { start_date, end_date } = periodToDates(
      Number.parseInt(year),
      quarter
    );
    const vatReturnPeriod = periodToDates(
      currentVatReturnPeriod.year,
      currentVatReturnPeriod.quarter
    );

    if (start_date.getMonth() + 1 < 7 && Number.parseInt(year) === 2021) {
      setError('period', {
        type: 'manual',
        message: `The allowed corrections are for periods starting after 01/07/2021`,
      });
      return false;
    }

    if (
      start_date > vatReturnPeriod.start_date ||
      end_date > vatReturnPeriod.start_date
    ) {
      setError('period', {
        type: 'manual',
        message: `The allowed periods are before begin date of current VAT Return`,
      });
      return false;
    }
    return true;
  };

  const periodToDates = (
    year: number,
    quarter: IQuarters
  ): {
    start_date: Date;
    end_date: Date;
  } => {
    let startMonth = quarter.starting_month - 1;
    let endMonth = quarter.ending_month - 1;
    return {
      start_date: new Date(Date.UTC(year, startMonth, 1, 0, 0, 0, 0)),
      end_date: new Date(Date.UTC(year, endMonth + 1, 0, 0, 0, 0, 0)),
    };
  };

  const msconChanges = (object: IVatCorrection) => {
    let found = false;
    let reduxData = getState(ReducerKeys.VATRETURN_DETAIL_REDUCER)
      .vatReturnDetail.mscon;
    let newMsconData = [];
    for (let item of reduxData) {
      if (item.country_id !== object.country_id) newMsconData.push(item);
      else {
        found = true;
        let obj = {
          ...item,
          total_correction: item.total_correction + object.amount,
          total_amount_due: item.total_amount_due + object.amount,
        };
        newMsconData.push(obj);
      }
    }

    if (!found) {
      let obj = {
        vat_return_version_id: 0,
        country_id: object.country_id,
        country: object.country,
        grand_total: 0,
        total_msid_goods: 0,
        total_msid_services: 0,
        total_msest_goods: 0,
        total_msest_services: 0,
        due_balance: 0,
        total_correction: object.amount,
        total_amount_due: object.amount,
      };
      newMsconData.push(obj);
    }

    dispatch(VatReturnDetailsActions.setMsconAction(newMsconData));
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      <GridLayout
        elements={[
          {
            element: renderYearField(errors, control),
            size: 12,
            id: 'year',
          },
          {
            element: renderPeriodField(
              errors,
              control,
              props.schemeType,
              watchYear
            ),
            size: 12,
            id: 'period',
          },
          {
            element: renderConsuptionField(errors, control),
            size: 12,
            id: 'consuption',
          },
          {
            element: renderVatAmountCorrectionField(
              errors,
              control,
              watchCountry
            ),
            size: 10,
            id: 'amount',
          },
          {
            element: renderCentsField(errors, control),
            size: 2,
            id: 'cents',
          },
          {
            element: (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ width: '100%' }}
              >
                <TranslatedText textMap={Translations.add} />
              </Button>
            ),
            size: 12,
            id: 'action',
          },
        ]}
      />
    </form>
  );
}

const renderPeriodField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  schemeType: SchemeTypes,
  watchYear: number | null
) => {
  const vatReturnDetailPeriod =
    VatReturnDetailsSelectors.useGetVatReturnDetailPeriod();
  const monthQuarters = VatReturnDetailsSelectors.useGetMonthQuarterList();
  const periodQuarters = VatReturnDetailsSelectors.useGetQuarters();

  let quarters = null;
  if (schemeType === SchemeTypes.IOSS) {
    quarters = Object.assign({}, monthQuarters);
  } else if (
    schemeType === SchemeTypes.EU ||
    schemeType === SchemeTypes.NON_EU
  ) {
    quarters = Object.assign({}, periodQuarters);
  }

  if (
    quarters &&
    quarters.data &&
    `${watchYear}` === `${vatReturnDetailPeriod.year}`
  ) {
    quarters.data = quarters.data.filter(
      (value) => value.quarter_id < vatReturnDetailPeriod.quarter.quarter_id
    );
  }

  let quarters_options = [];
  quarters_options.push({
    id: null,
    label: Translations.period_control.English,
  });
  if (quarters && quarters.status === StateStatus.Success)
    for (let quarter of quarters.data)
      quarters_options.push({
        id: quarter.quarter_id,
        label: quarter.short_text,
      });
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="period"
      label={<TranslatedText textMap={Translations.period_control} />}
      error={'period' in errors}
      name="period"
      control={control}
      rules={{ required: 'Required Field' }}
      options={quarters_options as any}
      helperText={errors.period && errors.period.message}
    />
  );
};

const renderYearField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  const vatReturnDetailPeriod =
    VatReturnDetailsSelectors.useGetVatReturnDetailPeriod();
  const currentDate = new Date();
  const currentYear = vatReturnDetailPeriod.year ?? currentDate.getFullYear();
  const startYear = 2021;
  const years_options = [];
  years_options.push({
    id: null,
    label: Translations.year_control.English,
  });
  for (let year = startYear; year <= currentYear; year++) {
    var obj = {
      id: `${year}`,
      label: `${year}`,
    };
    years_options.push(obj);
  }

  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="year"
      label={<TranslatedText textMap={Translations.year_control} />}
      error={'year' in errors}
      name="year"
      control={control}
      rules={{ required: 'Required Field' }}
      options={years_options as any}
      helperText={errors.year && errors.year.message}
    />
  );
};

const renderConsuptionField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  let countries = getState(ReducerKeys.VATRETURN_DETAIL_REDUCER).countries;
  let correctionsCountries =
    VatReturnDetailsSelectors.useGetCorrectionsCountries();

  let countries_list = [];
  countries_list.push({
    id: null,
    label: Translations.consuption_control.English,
  });
  if (
    correctionsCountries &&
    correctionsCountries.status === StateStatus.Success &&
    correctionsCountries.data.length > 0
  ) {
    countries.data.forEach((value: any, index: number) => {
      countries_list.push({
        id: value.country_id,
        label: value.country,
      });
    });
  }
  return (
    <AppSelect
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="consuptionCountry"
      label={<TranslatedText textMap={Translations.consuption_control} />}
      error={'consuptionCountry' in errors}
      name="consuptionCountry"
      control={control}
      rules={{ required: 'Required Field' }}
      options={countries_list as any}
      helperText={errors.consuptionCountry && errors.consuptionCountry.message}
    />
  );
};

const renderVatAmountCorrectionField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  watchCountry: string | null
) => {
  let due_balance: number | null = null;
  if (watchCountry) {
    let reduxData = getState(ReducerKeys.VATRETURN_DETAIL_REDUCER)
      .correctionsCountries.data;
    try {
      const item = reduxData.find(
        (item: { country_id: number }) =>
          item.country_id === Number(watchCountry)
      );
      due_balance = item.due_balance;
    } catch (error) {
      due_balance = null;
    }
  }

  return (
    <Fragment>
      <AppTextField
        variant="outlined"
        margin="normal"
        type={'number'}
        required
        fullWidth
        id="amount"
        label={<TranslatedText textMap={Translations.amount_control} />}
        error={'amount' in errors}
        name="amount"
        control={control}
        rules={{
          required: 'Required Field',
        }}
        helperText={
          errors.amount
            ? errors.amount.message
            : due_balance
            ? `${Translations.amount_error.English}${due_balance}`
            : ''
        }
      />
    </Fragment>
  );
};

const renderCentsField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      type="number"
      id="cents"
      label={<TranslatedText textMap={Translations.cents_control} />}
      error={'cents' in errors}
      name="cents"
      control={control}
      rules={{
        required: 'Required',
        min: {
          value: 0,
          message: Translations.invalid_cents_error.English,
        },
        maxLength: {
          value: 2,
          message: Translations.invalid_cents_error.English,
        },
      }}
      helperText={errors.cents && errors.cents.message}
    />
  );
};
